import { CalendarOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, DatePicker, Input, InputRef, Popover, Row, Space, Tag, Typography } from 'antd';
import Table, { ColumnsType } from 'antd/lib/table';
import { FC, useEffect, useRef, useState } from 'react';
import { PeptideSet } from '../types/analysisTypes';
import { ColumnType, FilterConfirmProps } from 'antd/lib/table/interface';
import Highlighter from 'react-highlight-words';
import { aryballeColorPalette, defaultColorPalette, getColormap } from '../compute/colormap';
import { getPeptideSetType, getSortedUniqueSlice, renderColoredTag, renderDeviceID, renderExposureIcon, renderFwVersion, renderPeptideSetType, renderSwVersion, renderTimeDuration, renderTimestamp, spotsGrid2PeptideSet } from '../compute/utils';
import { useQueryParam } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { setVisibleDeleteRuns } from '../features/analysisConfig/sessionInfoSlice';
import { useAppDispatch } from '../app/hooks';
import { useAuth0 } from '@auth0/auth0-react';
import { WithFeatureFlagsCheck } from '../with_feature_flags_check';
import { FeatureFlag, UserClaimsWithTSDB } from '../types/userType';
import { WebappRecord } from '../types/webappTypes';

interface DataType {
  key: string;
  id: string;
  uid: string;
  name: string;
  description: string;
  framesCount: number;
  timestampStart: number;
  timestampEnd: number;
  userEmail: string;

  fwVersion: string;
  deviceID: string;
  deviceShellSerial: string;
  deviceCoreSensorSerial: string;
  peptideSetType: PeptideSet;
  spotsGrid: number[];

  calibrationExposure: number;
}

type DataIndex = keyof DataType;

export const RecordsTable: FC<{
  records: WebappRecord[];
  selectedRecords: string[];
  setSelectedRecords: React.Dispatch<React.SetStateAction<string[]>>;
  selectedDeleteRuns?: string[];
  setSelectedDeleteRuns?: React.Dispatch<React.SetStateAction<string[]>>;
}> = (props) => {
  const { records, selectedRecords, setSelectedRecords, selectedDeleteRuns, setSelectedDeleteRuns } = props;

  const [dataSource, setDataSource] = useState<DataType[]>([]);

  const searchInput = useRef<InputRef>(null);

  const [recordIDSearchText, setRecordIDSearchText] = useQueryParam('record_id', '');
  const [coreSensorSNSearchText, setCoreSensorSNSearchText] = useQueryParam('cs_sn', '');
  const [userNameSearchText, setUserNameSearchText] = useQueryParam('user_email', '');
  const [shellSNSearchText, setShellSNSearchText] = useQueryParam('shell_sn', '');
  const [runNameSearchText, setRunNameSearchText] = useQueryParam('run_name', '');
  const [descriptionSearchText, setDescriptionSearchText] = useQueryParam('description', '');
  const [selectedRunsQueryParam, setSelectedRunsQueryParam] = useQueryParam('selected_records', '');

  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAuth0();

  const [userInfo, setUserInfo] = useState<UserClaimsWithTSDB | null>(null);

  useEffect(() => {
    if (!user || !isAuthenticated) setUserInfo(null);
    else setUserInfo(user as UserClaimsWithTSDB);
  }, [isAuthenticated, user]);

  useEffect(() => {
    setSelectedRunsQueryParam(selectedRecords.join(';'));
  }, [selectedRecords]);

  // Executes ones on the reception of runs (after /list_runs)
  useEffect(() => {
    let _dataSource = records.map((rec) => {
      const cmap = getColormap(Array.from(new Set([rec.Name])));
      return {
        id: rec.ID,
        key: rec.ID,
        uid: rec.ID,
        name: rec.Name,
        nameRendered: (
          <Typography.Text style={{ maxWidth: '200px' }} ellipsis={{ tooltip: rec.Name }}>
            {rec.Name}
          </Typography.Text>
        ),
        description: rec.Description,
        framesCount: rec.SensogramNFrames,
        timestampStart: rec.AbsoluteTimestamp,
        timestampEnd: 0,
        itemsRendered: <div style={{ overflow: 'scroll', maxHeight: '150px' }}>{renderColoredTag(defaultColorPalette[cmap[rec.Name]], <>{rec.Name}</>, 0)}</div>,
        dateTimeRendered: renderTimestamp(rec.AbsoluteTimestamp),
        userEmail: rec.UserEmail,

        // New fields (2022-09-23)
        fwVersion: rec.DeviceFwVersion,
        deviceID: rec.DeviceCommonName,
        deviceShellSerial: rec.DeviceShellSerial, // NOA22{1/2} is clunky and does not provide much information
        deviceCoreSensorSerial: rec.DeviceCoreSensorSerial, // CS22{1/2} is clunky and does not provide much information
        peptideSetType: getPeptideSetType(rec.DeviceSpotsgrid),
        spotsGrid: rec.DeviceSpotsgrid,

        calibrationExposure: rec.DeviceCameraExposure,
      } as DataType;
    });
    setDataSource(_dataSource);
  }, [records]);

  let uniquePeptideSets = Array.from(new Set(records.map((r) => getPeptideSetType(r.DeviceSpotsgrid))));

  const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters: () => void, confirm: () => void) => {
    clearFilters();
    confirm();
  };

  const getColumnSearchProps = (dataIndex: DataIndex, isTagged: boolean, queryParam: string, setQueryParam: (newValue: string) => void): ColumnType<DataType> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button type="primary" onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>
            Search
          </Button>
          <Button
            onClick={() => {
              setQueryParam('');
              clearFilters && handleReset(clearFilters, confirm);
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) => {
      setQueryParam('' + value);
      return record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase());
    },
    filterDropdownProps: {
      onOpenChange: (open) => {
        if (open) setTimeout(() => searchInput.current?.select(), 100);
      },
    },
    render: (text) => {
      let children = queryParam !== '' ? <Highlighter highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }} searchWords={[queryParam]} autoEscape textToHighlight={text ? text.toString() : ''} /> : text;
      return (
        <Typography.Text style={{ maxWidth: '200px' }} ellipsis={{ tooltip: text }}>
          {isTagged ? <Tag>{children}</Tag> : children}
        </Typography.Text>
      );
    },
  });

  const columns: ColumnsType<DataType> = [
    {
      title: 'Record ID',
      dataIndex: 'id',
      key: 'id',
      defaultFilteredValue: recordIDSearchText ? [recordIDSearchText] : null,
      sorter: (a, b) => (a.id > b.id ? 1 : -1),
      ...getColumnSearchProps('id', false, recordIDSearchText, setRecordIDSearchText),
    },
    {
      title: 'CS',
      dataIndex: 'deviceCoreSensorSerial',
      key: 'deviceCoreSensorSerial',
      defaultFilteredValue: coreSensorSNSearchText ? [coreSensorSNSearchText] : null,
      sorter: (a, b) => (a.deviceCoreSensorSerial > b.deviceCoreSensorSerial ? 1 : -1),
      ...getColumnSearchProps('deviceCoreSensorSerial', true, coreSensorSNSearchText, setCoreSensorSNSearchText),
    },
    {
      title: 'NeOse',
      dataIndex: 'deviceShellSerial',
      key: 'deviceShellSerial',
      defaultFilteredValue: shellSNSearchText ? [shellSNSearchText] : null,
      sorter: (a, b) => (a.deviceShellSerial > b.deviceShellSerial ? 1 : -1),
      ...getColumnSearchProps('deviceShellSerial', true, shellSNSearchText, setShellSNSearchText),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      defaultFilteredValue: runNameSearchText ? [runNameSearchText] : null,
      sorter: (a, b) => (a.name > b.name ? 1 : -1),
      ...getColumnSearchProps('name', false, runNameSearchText, setRunNameSearchText),
    },
    {
      title: 'User',
      dataIndex: 'userEmail',
      key: 'userEmail',
      defaultFilteredValue: userNameSearchText ? [userNameSearchText] : null,
      sorter: (a, b) => (a.userEmail > b.userEmail ? 1 : -1),
      ...getColumnSearchProps('userEmail', false, userNameSearchText, setUserNameSearchText),
    },
    {
      title: 'Date/Time',
      dataIndex: 'dateTimeRendered',
      key: 'dateTimeRendered',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.timestampStart - b.timestampStart,
    },
    {
      title: 'Total Frames',
      dataIndex: 'framesCount',
      key: 'framesCount',
      sorter: (a, b) => a.framesCount - b.framesCount,
    },
    {
      title: 'Decription',
      dataIndex: 'description',
      key: 'description',
      defaultFilteredValue: descriptionSearchText ? [descriptionSearchText] : null,
      sorter: (a, b) => (a.description > b.description ? 1 : -1),
      ...getColumnSearchProps('description', false, descriptionSearchText, setDescriptionSearchText),
    },
    {
      title: 'Flags',
      key: 'flags',
      responsive: ['md'],
      filters: uniquePeptideSets.map((e) => {
        return { text: renderPeptideSetType(e), value: e };
      }),
      onFilter: (value, record) => {
        return record.peptideSetType === value;
      },
      render(value, record, index) {
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            {renderPeptideSetType(record.peptideSetType)} {renderFwVersion(record.fwVersion)} {renderExposureIcon(record.calibrationExposure)}
          </div>
        );
      },
    },
  ];

  if (selectedDeleteRuns && setSelectedDeleteRuns && userInfo && userInfo.feature_flags.includes(FeatureFlag.AADeleteRunsEnabled)) {
    columns.push({
      title: (
        <Row justify="center">
          <WithFeatureFlagsCheck ff={FeatureFlag.AADeleteRunsEnabled}>
            <FontAwesomeIcon icon="trash" style={{ color: aryballeColorPalette.gray }} />
          </WithFeatureFlagsCheck>
        </Row>
      ),
      dataIndex: 'uid',
      key: 'uid',
      render: (value: string) => {
        console.log('finding selected record index');
        const findIdInSelectedRecords = selectedRecords.findIndex((uid) => uid === value);
        return (
          <Row justify="center">
            {findIdInSelectedRecords === -1 ? (
              <FontAwesomeIcon
                icon="trash"
                style={{
                  cursor: 'pointer',
                  color: selectedDeleteRuns && selectedDeleteRuns.includes(value) ? defaultColorPalette.red : aryballeColorPalette.gray,
                }}
                onClick={() => {
                  dispatch(setVisibleDeleteRuns(true));
                  if (selectedDeleteRuns.includes(value)) {
                    setSelectedDeleteRuns(selectedDeleteRuns.filter((el) => el !== value));
                  } else setSelectedDeleteRuns([...selectedDeleteRuns, value]);
                }}
              />
            ) : (
              <Popover placement="topRight" content="You can't delete this run because it is selected for analysis">
                <FontAwesomeIcon
                  icon="trash"
                  style={{
                    cursor: 'not-allowed',
                    color: '#e2e2e2',
                  }}
                />
              </Popover>
            )}
          </Row>
        );
      },
    });
  }

  return (
    <div style={{ width: '100%' }}>
      <Table
        style={{ width: '100%' }}
        columns={columns}
        dataSource={dataSource}
        size="small"
        scroll={{ scrollToFirstRowOnChange: true, x: '100%' }}
        pagination={{
          defaultPageSize: 20,
        }}
        expandable={{
          expandedRowRender: (record) => (
            <>
              <Typography.Paragraph>
                <b>Run UID: </b> {record.uid}
                <br />
                <b>Device ID: </b>
                {renderDeviceID(record.deviceID)}
                <br />
                <b>Started: </b> {renderTimestamp(record.timestampStart)}
                <br />
                <b>Ended: </b> {renderTimestamp(record.timestampEnd)} (ended {renderTimeDuration(new Date(record.timestampEnd), new Date())} ago)
                <br />
                <b>Duration: </b> {renderTimeDuration(new Date(record.timestampStart), new Date(record.timestampEnd))}
                <br />
                <b>FW Version: </b> {record.fwVersion} {renderFwVersion(record.fwVersion)}
                <br />
                <b>Exposure: </b> {record.calibrationExposure} μs {renderExposureIcon(record.calibrationExposure)}
                <br />
                <b>Peptides: </b> {Array.from(spotsGrid2PeptideSet(record.spotsGrid)).join(', ')} {renderPeptideSetType(record.peptideSetType)}
              </Typography.Paragraph>
            </>
          ),
          rowExpandable: () => true,
        }}
        rowSelection={{
          type: 'checkbox',
          onChange: (_selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
            const findIdInSelectedDeleteRuns = selectedRows && selectedRows[selectedRows.length - 1] ? selectedDeleteRuns?.includes(selectedRows[selectedRows.length - 1].uid) : undefined;
            if (!findIdInSelectedDeleteRuns) {
              console.log('changing selected record');
              let _selectedRecords = selectedRows.map((record) => record.uid);
              setSelectedRecords(_selectedRecords);
            }
          },
          selectedRowKeys: selectedRecords,
          getCheckboxProps: (record: DataType) => ({
            disabled: selectedDeleteRuns?.includes(record.uid),
          }),
        }}
      ></Table>
    </div>
  );
};
